/**
 * Product Labels Tab — Kullanıcının tasarladığı etiket dosyalarını
 * (PNG/JPG/PDF/SVG) ürüne bağlar. Argox IX4-250 için ayarlanabilir
 * genişlik/yükseklik ile yazdırılabilir PDF üretir.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Upload, Printer, Trash2, Tag, FileText, Loader2, Image as ImageIcon, Copy,
} from "lucide-react";
import { toast } from "sonner";

const KIND_LABELS = {
  beden: "Beden Etiketi",
  yikama: "Yıkama Talimatı",
  ana: "Ana Etiket",
  diger: "Diğer",
};
const KIND_COLORS = {
  beden: "bg-blue-950/30 border-blue-900/60 text-blue-300",
  yikama: "bg-emerald-950/30 border-emerald-900/60 text-emerald-300",
  ana: "bg-blue-950/30 border-blue-900/60 text-blue-300",
  diger: "bg-slate-800 border-slate-700 text-slate-300",
};

// Yaygın etiket şeridi enleri (mm)
const WIDTH_PRESETS = [10, 20, 30, 40, 50, 60, 100];

export default function LabelsTab({ productId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    kind: "beden", name: "", width_mm: 40, height_mm: 30,
  });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [printLabel, setPrintLabel] = useState(null);
  const [printForm, setPrintForm] = useState({ width_mm: 40, height_mm: 30, copies: 1 });
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/product-labels/product/${productId}`);
      setItems(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [productId]);

  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!file) { toast.error("Dosya seçin"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("product_id", productId);
      fd.append("kind", uploadForm.kind);
      fd.append("name", uploadForm.name || file.name);
      fd.append("width_mm", String(uploadForm.width_mm));
      fd.append("height_mm", String(uploadForm.height_mm));
      fd.append("file", file);
      const { data } = await api.post("/product-labels/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`"${data.name}" yüklendi`);
      setUploadOpen(false);
      setFile(null);
      setUploadForm({ kind: "beden", name: "", width_mm: 40, height_mm: 30 });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Etiketi silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/product-labels/${id}`);
      toast.success("Silindi");
      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openPrint = (lb) => {
    setPrintLabel(lb);
    setPrintForm({
      width_mm: lb.width_mm || 40,
      height_mm: lb.height_mm || 30,
      copies: 1,
    });
  };

  const doPrint = () => {
    if (!printLabel) return;
    const base = process.env.REACT_APP_BACKEND_URL;
    const qs = new URLSearchParams({
      copies: String(printForm.copies || 1),
      width_mm: String(printForm.width_mm),
      height_mm: String(printForm.height_mm),
    }).toString();
    // Yeni sekmede PDF açılır, kullanıcı Ctrl+P → Argox sürücüsünü seçer
    const url = `${base}/api/product-labels/print/${printLabel.id}?${qs}`;
    window.open(url, "_blank");
    setPrintLabel(null);
  };

  return (
    <div className="space-y-4">
      <Card className="rounded-sm bg-card border-border p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs uppercase font-mono text-muted-foreground">Etiket Kütüphanesi</div>
            <div className="text-[11px] text-muted-foreground mt-1 font-mono">
              Argox IX4-250 · PNG / JPG / PDF / SVG · max 15MB
            </div>
          </div>
          <Button
            data-testid="upload-label-btn"
            onClick={() => setUploadOpen(true)}
            className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold"
            size="sm"
          >
            <Upload className="w-4 h-4 mr-1" /> Etiket Yükle
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-border rounded-sm">
            <Tag className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
            <div className="text-sm text-muted-foreground">
              Henüz etiket yüklenmemiş.
            </div>
            <div className="text-[11px] text-muted-foreground/70 mt-1 font-mono">
              Beden / Yıkama / Ana etiket dosyalarınızı yükleyin, Argobar veya PDF ile yazdırın.
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((lb) => (
              <LabelCard key={lb.id} label={lb} onPrint={openPrint} onDelete={remove} />
            ))}
          </div>
        )}
      </Card>

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="rounded-sm sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Etiket Yükle</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase font-mono text-muted-foreground">Etiket Tipi</Label>
              <Select
                value={uploadForm.kind}
                onValueChange={(v) => setUploadForm({ ...uploadForm, kind: v })}
              >
                <SelectTrigger data-testid="upload-kind" className="rounded-sm mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(KIND_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase font-mono text-muted-foreground">Etiket Adı (opsiyonel)</Label>
              <Input
                data-testid="upload-name"
                value={uploadForm.name}
                onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })}
                placeholder="Örn: Standart Beden Etiketi"
                className="rounded-sm mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase font-mono text-muted-foreground">Genişlik (mm)</Label>
                <Select
                  value={String(uploadForm.width_mm)}
                  onValueChange={(v) => setUploadForm({ ...uploadForm, width_mm: parseFloat(v) })}
                >
                  <SelectTrigger data-testid="upload-width" className="rounded-sm mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WIDTH_PRESETS.map((w) => (
                      <SelectItem key={w} value={String(w)}>{w} mm ({(w / 10).toFixed(0)} cm)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase font-mono text-muted-foreground">Yükseklik (mm)</Label>
                <Input
                  data-testid="upload-height"
                  type="number"
                  min={5}
                  max={300}
                  value={uploadForm.height_mm}
                  onChange={(e) => setUploadForm({ ...uploadForm, height_mm: parseFloat(e.target.value) || 30 })}
                  className="rounded-sm mt-1 font-mono"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase font-mono text-muted-foreground">Dosya</Label>
              <input
                ref={fileInputRef}
                data-testid="upload-file"
                type="file"
                accept="image/*,.pdf,.svg"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  setFile(f || null);
                  if (f && !uploadForm.name) {
                    setUploadForm((p) => ({ ...p, name: f.name.replace(/\.[^.]+$/, "") }));
                  }
                }}
                className="w-full text-xs mt-1 bg-input border border-border rounded-sm px-3 py-2 file:mr-3 file:bg-blue-600 file:text-white file:border-0 file:rounded-sm file:px-3 file:py-1 file:text-xs file:font-semibold file:cursor-pointer"
              />
              {file && (
                <div className="text-[11px] text-muted-foreground mt-1 font-mono">
                  {file.name} · {(file.size / 1024).toFixed(1)} KB
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)} className="rounded-sm">
              İptal
            </Button>
            <Button
              data-testid="upload-save"
              onClick={upload}
              disabled={uploading || !file}
              className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold"
            >
              {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
              Yükle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print dialog */}
      <Dialog open={!!printLabel} onOpenChange={(o) => !o && setPrintLabel(null)}>
        <DialogContent className="rounded-sm sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Yazdır — {printLabel?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase font-mono text-muted-foreground">Genişlik (mm)</Label>
                <Select
                  value={String(printForm.width_mm)}
                  onValueChange={(v) => setPrintForm({ ...printForm, width_mm: parseFloat(v) })}
                >
                  <SelectTrigger data-testid="print-width" className="rounded-sm mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WIDTH_PRESETS.map((w) => (
                      <SelectItem key={w} value={String(w)}>{w} mm</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase font-mono text-muted-foreground">Yükseklik (mm)</Label>
                <Input
                  data-testid="print-height"
                  type="number"
                  min={5}
                  max={300}
                  value={printForm.height_mm}
                  onChange={(e) => setPrintForm({ ...printForm, height_mm: parseFloat(e.target.value) || 30 })}
                  className="rounded-sm mt-1 font-mono"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase font-mono text-muted-foreground">Kopya Adedi</Label>
              <Input
                data-testid="print-copies"
                type="number"
                min={1}
                max={500}
                value={printForm.copies}
                onChange={(e) => setPrintForm({ ...printForm, copies: parseInt(e.target.value, 10) || 1 })}
                className="rounded-sm mt-1 font-mono"
              />
            </div>
            <div className="text-[11px] font-mono text-muted-foreground bg-muted/30 rounded-sm p-2 space-y-1">
              <div className="text-emerald-400 font-semibold">✓ Önerilen yöntem (Argobar):</div>
              <div>1. "İptal" → kartta yeşil <b>ARGOBAR</b> butonuna tıklayın → orijinal dosya indirilir</div>
              <div>2. Argobar'ı açın → <b>Grafik Ekle</b> → indirdiğiniz dosyayı seçin</div>
              <div>3. Argobar'da tam boyut ayarlayıp <b>Yazdır</b></div>
              <div className="pt-1 mt-1 border-t border-border/40 text-amber-400">PDF yöntemi: Chrome açılınca yazıcı = <b>Argox IX4-250</b>, "Paper Size" = <b>Custom {printForm.width_mm}×{printForm.height_mm}mm</b>, "Scale" = <b>100%</b>, "Margins" = <b>None</b>.</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPrintLabel(null)} className="rounded-sm">
              İptal
            </Button>
            <Button
              data-testid="print-open"
              onClick={doPrint}
              className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold"
            >
              <Printer className="w-4 h-4 mr-1" /> PDF Aç
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LabelCard({ label, onPrint, onDelete }) {
  const [previewErr, setPreviewErr] = useState(false);
  const base = process.env.REACT_APP_BACKEND_URL;
  const isPdf = (label.content_type || "").includes("pdf");
  const previewUrl = `${base}/api/product-labels/download/${label.id}`;

  return (
    <div
      data-testid={`label-card-${label.id}`}
      className="bg-muted/20 border border-border rounded-sm overflow-hidden flex flex-col"
    >
      <div className="aspect-[4/3] bg-slate-950 flex items-center justify-center relative overflow-hidden">
        {isPdf || previewErr ? (
          <div className="flex flex-col items-center gap-1 text-muted-foreground">
            <FileText className="w-8 h-8" />
            <div className="text-[10px] font-mono">{isPdf ? "PDF" : "Önizleme yok"}</div>
          </div>
        ) : (
          <img
            src={previewUrl}
            alt={label.name}
            className="max-w-full max-h-full object-contain"
            crossOrigin="use-credentials"
            onError={() => setPreviewErr(true)}
          />
        )}
        <div className={`absolute top-2 left-2 px-1.5 py-0.5 text-[10px] uppercase font-mono border rounded-sm ${KIND_COLORS[label.kind] || KIND_COLORS.diger}`}>
          {KIND_LABELS[label.kind] || label.kind}
        </div>
      </div>
      <div className="p-3 space-y-2 flex-1 flex flex-col justify-between">
        <div>
          <div className="text-sm font-semibold text-foreground truncate" title={label.name}>
            {label.name}
          </div>
          <div className="text-[10px] text-muted-foreground font-mono mt-0.5">
            {label.width_mm} × {label.height_mm} mm · {(label.size / 1024).toFixed(0)} KB
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            data-testid={`argobar-download-${label.id}`}
            size="sm"
            onClick={() => {
              const a = document.createElement("a");
              a.href = previewUrl;
              a.download = label.original_filename || `${label.name}.dosya`;
              a.click();
            }}
            className="flex-1 bg-emerald-600 hover:bg-emerald-500 rounded-sm text-xs uppercase font-semibold"
            title="Argobar için orijinal dosyayı indir — Argobar'da 'Grafik ekle' ile aç, tam boyut yazdır"
          >
            <FileText className="w-3.5 h-3.5 mr-1" /> Argobar
          </Button>
          <Button
            data-testid={`print-label-${label.id}`}
            size="sm"
            onClick={() => onPrint(label)}
            className="flex-1 bg-blue-600 hover:bg-blue-500 rounded-sm text-xs uppercase font-semibold"
            title="Tarayıcı üzerinden PDF olarak yazdır (Chrome/Edge)"
          >
            <Printer className="w-3.5 h-3.5 mr-1" /> PDF
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => window.open(previewUrl, "_blank")}
            className="text-slate-400 hover:text-blue-400 hover:bg-blue-950/30 rounded-sm px-2"
            title="Aç"
          >
            <ImageIcon className="w-3.5 h-3.5" />
          </Button>
          <Button
            data-testid={`delete-label-${label.id}`}
            size="sm"
            variant="ghost"
            onClick={() => onDelete(label.id)}
            className="text-slate-500 hover:text-red-400 hover:bg-red-950/30 rounded-sm px-2"
            title="Sil"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
